/**
 * Curve modifier — pure typed-array core (Three.js-free).
 *
 * Deforms the owner mesh so one chosen local axis follows an evaluated target
 * curve path. Topology is unchanged: only vertex positions move, normals are
 * cleared, and provenance stays identity.
 *
 * v1 behavior:
 * - Runtime-only scene-object modifier (no .blend modifier extraction)
 * - Uses the target curve's evaluated arc length in owner-local space
 * - Source length is preserved: distance from the source mesh's leading bound
 *   maps 1:1 onto distance along the curve
 * - Optional curve-radius scaling multiplies cross-section offsets by the
 *   target curve's interpolated radius
 */

import type { BlendModifier } from "../../blend-reader/types/BlendModifier.ts";
import { identityProvenance } from "../../modeling/provenance.ts";
import type {
  ModifierCurvePath,
  ModifierEvaluationInput,
  ModifierGeometry,
} from "./modifierTypes.ts";

interface SampledFrame {
  px: number;
  py: number;
  pz: number;
  nx: number;
  ny: number;
  nz: number;
  bx: number;
  by: number;
  bz: number;
  normalScale: number;
  binormalScale: number;
  radius: number;
}

function normalizeOr(
  x: number,
  y: number,
  z: number,
  fallback: readonly [number, number, number],
): [number, number, number] {
  const len = Math.sqrt(x * x + y * y + z * z);
  if (len < 1e-8) return [fallback[0], fallback[1], fallback[2]];
  return [x / len, y / len, z / len];
}

function sampleCurvePath(
  path: ModifierCurvePath,
  distance: number,
  out: SampledFrame,
): boolean {
  const sampleCount = path.cumulativeLengths.length;
  if (sampleCount === 0) return false;

  const totalLength = path.cumulativeLengths[sampleCount - 1] ?? 0;
  let d = distance;
  if (path.cyclic && totalLength > 1e-8) {
    d %= totalLength;
    if (d < 0) d += totalLength;
  } else {
    d = Math.max(0, Math.min(totalLength, d));
  }

  if (sampleCount === 1 || totalLength <= 1e-8) {
    const tangent = normalizeOr(
      path.tangents[0],
      path.tangents[1],
      path.tangents[2],
      [0, 1, 0],
    );
    const normal = normalizeOr(
      path.normals[0],
      path.normals[1],
      path.normals[2],
      [1, 0, 0],
    );
    const binormal = normalizeOr(
      tangent[1] * normal[2] - tangent[2] * normal[1],
      tangent[2] * normal[0] - tangent[0] * normal[2],
      tangent[0] * normal[1] - tangent[1] * normal[0],
      [0, 0, 1],
    );
    out.px = path.positions[0];
    out.py = path.positions[1];
    out.pz = path.positions[2];
    out.nx = normal[0];
    out.ny = normal[1];
    out.nz = normal[2];
    out.bx = binormal[0];
    out.by = binormal[1];
    out.bz = binormal[2];
    out.normalScale = path.normalScales[0] ?? 1;
    out.binormalScale = path.binormalScales[0] ?? 1;
    out.radius = path.radii[0] ?? 1;
    return true;
  }

  let hi = sampleCount - 1;
  let lo = 0;
  while (lo < hi) {
    const mid = (lo + hi) >>> 1;
    if (path.cumulativeLengths[mid] < d) lo = mid + 1;
    else hi = mid;
  }
  const i1 = Math.min(lo, sampleCount - 1);
  const i0 = Math.max(0, i1 - 1);
  const d0 = path.cumulativeLengths[i0];
  const d1 = path.cumulativeLengths[i1];
  const t = d1 > d0 ? (d - d0) / (d1 - d0) : 0;

  const a = i0 * 3;
  const b = i1 * 3;
  out.px = path.positions[a] + (path.positions[b] - path.positions[a]) * t;
  out.py =
    path.positions[a + 1] + (path.positions[b + 1] - path.positions[a + 1]) * t;
  out.pz =
    path.positions[a + 2] + (path.positions[b + 2] - path.positions[a + 2]) * t;

  const tangent = normalizeOr(
    path.tangents[a] + (path.tangents[b] - path.tangents[a]) * t,
    path.tangents[a + 1] + (path.tangents[b + 1] - path.tangents[a + 1]) * t,
    path.tangents[a + 2] + (path.tangents[b + 2] - path.tangents[a + 2]) * t,
    [0, 1, 0],
  );
  let nx = path.normals[a] + (path.normals[b] - path.normals[a]) * t;
  let ny =
    path.normals[a + 1] + (path.normals[b + 1] - path.normals[a + 1]) * t;
  let nz =
    path.normals[a + 2] + (path.normals[b + 2] - path.normals[a + 2]) * t;
  const ndot = nx * tangent[0] + ny * tangent[1] + nz * tangent[2];
  nx -= tangent[0] * ndot;
  ny -= tangent[1] * ndot;
  nz -= tangent[2] * ndot;
  const normal = normalizeOr(nx, ny, nz, [1, 0, 0]);
  const binormal = normalizeOr(
    tangent[1] * normal[2] - tangent[2] * normal[1],
    tangent[2] * normal[0] - tangent[0] * normal[2],
    tangent[0] * normal[1] - tangent[1] * normal[0],
    [0, 0, 1],
  );

  out.nx = normal[0];
  out.ny = normal[1];
  out.nz = normal[2];
  out.bx = binormal[0];
  out.by = binormal[1];
  out.bz = binormal[2];
  out.normalScale =
    path.normalScales[i0] + (path.normalScales[i1] - path.normalScales[i0]) * t;
  out.binormalScale =
    path.binormalScales[i0] +
    (path.binormalScales[i1] - path.binormalScales[i0]) * t;
  out.radius = path.radii[i0] + (path.radii[i1] - path.radii[i0]) * t;
  return true;
}

function axisIndex(axis: string): 0 | 1 | 2 {
  if (axis.endsWith("x")) return 0;
  if (axis.endsWith("y")) return 1;
  return 2;
}

export function curveCore(
  geo: ModifierGeometry,
  mod: Extract<BlendModifier, { kind: "curve" }>,
  input?: ModifierEvaluationInput,
): ModifierGeometry {
  if (!mod.targetObjectName || !input?.curveTargets) return geo;
  const path = input.curveTargets[mod.targetObjectName];
  if (!path || path.positions.length === 0) return geo;

  const totalLength =
    path.cumulativeLengths[path.cumulativeLengths.length - 1] ?? 0;
  if (totalLength <= 1e-8) return geo;

  const positions = geo.positions;
  const vertCount = positions.length / 3;
  if (vertCount === 0) {
    return { ...geo, provenance: identityProvenance(0) };
  }

  const axis = mod.deformAxis ?? "x";
  const alongIdx = axisIndex(axis);
  let minAlong = Infinity;
  let maxAlong = -Infinity;
  for (let i = alongIdx; i < positions.length; i += 3) {
    const v = positions[i];
    if (v < minAlong) minAlong = v;
    if (v > maxAlong) maxAlong = v;
  }

  const newPositions = new Float32Array(positions.length);
  const sampled: SampledFrame = {
    px: 0,
    py: 0,
    pz: 0,
    nx: 1,
    ny: 0,
    nz: 0,
    bx: 0,
    by: 0,
    bz: 1,
    normalScale: 1,
    binormalScale: 1,
    radius: 1,
  };

  for (let vi = 0; vi < vertCount; vi++) {
    const o = vi * 3;
    const x = positions[o];
    const y = positions[o + 1];
    const z = positions[o + 2];

    let distance = 0;
    switch (axis) {
      case "x":
        distance = x - minAlong;
        break;
      case "-x":
        distance = maxAlong - x;
        break;
      case "y":
        distance = y - minAlong;
        break;
      case "-y":
        distance = maxAlong - y;
        break;
      case "z":
        distance = z - minAlong;
        break;
      case "-z":
        distance = maxAlong - z;
        break;
    }

    if (!sampleCurvePath(path, distance, sampled)) {
      newPositions[o] = x;
      newPositions[o + 1] = y;
      newPositions[o + 2] = z;
      continue;
    }

    const radiusScale = mod.useCurveRadius ? sampled.radius : 1;
    const nScale = sampled.normalScale * radiusScale;
    const bScale = sampled.binormalScale * radiusScale;

    switch (axis) {
      case "x":
        newPositions[o] =
          sampled.px + y * sampled.nx * nScale + z * sampled.bx * bScale;
        newPositions[o + 1] =
          sampled.py + y * sampled.ny * nScale + z * sampled.by * bScale;
        newPositions[o + 2] =
          sampled.pz + y * sampled.nz * nScale + z * sampled.bz * bScale;
        break;
      case "-x":
        newPositions[o] =
          sampled.px + y * sampled.nx * nScale - z * sampled.bx * bScale;
        newPositions[o + 1] =
          sampled.py + y * sampled.ny * nScale - z * sampled.by * bScale;
        newPositions[o + 2] =
          sampled.pz + y * sampled.nz * nScale - z * sampled.bz * bScale;
        break;
      case "y":
        newPositions[o] =
          sampled.px + x * sampled.bx * bScale + z * sampled.nx * nScale;
        newPositions[o + 1] =
          sampled.py + x * sampled.by * bScale + z * sampled.ny * nScale;
        newPositions[o + 2] =
          sampled.pz + x * sampled.bz * bScale + z * sampled.nz * nScale;
        break;
      case "-y":
        newPositions[o] =
          sampled.px + x * sampled.bx * bScale - z * sampled.nx * nScale;
        newPositions[o + 1] =
          sampled.py + x * sampled.by * bScale - z * sampled.ny * nScale;
        newPositions[o + 2] =
          sampled.pz + x * sampled.bz * bScale - z * sampled.nz * nScale;
        break;
      case "z":
        newPositions[o] =
          sampled.px + x * sampled.nx * nScale + y * sampled.bx * bScale;
        newPositions[o + 1] =
          sampled.py + x * sampled.ny * nScale + y * sampled.by * bScale;
        newPositions[o + 2] =
          sampled.pz + x * sampled.nz * nScale + y * sampled.bz * bScale;
        break;
      case "-z":
        newPositions[o] =
          sampled.px + x * sampled.nx * nScale - y * sampled.bx * bScale;
        newPositions[o + 1] =
          sampled.py + x * sampled.ny * nScale - y * sampled.by * bScale;
        newPositions[o + 2] =
          sampled.pz + x * sampled.nz * nScale - y * sampled.bz * bScale;
        break;
    }
  }

  return {
    ...geo,
    positions: newPositions,
    normals: null,
    provenance: identityProvenance(vertCount),
  };
}
